import { useState, useEffect, useCallback, useRef } from 'react';
import { apiService } from '../services/api';

export interface AgentHealthStatus {
  agentId: string;
  status: 'healthy' | 'unhealthy' | 'checking' | 'unknown';
  lastChecked: Date | null;
  responseTime?: number;
  error?: string;
  disabledInConfig?: boolean;
  capabilities?: {
    basicConnectivity: boolean;
    conversationalCapability: boolean;
    supportsStreaming: boolean;
    supportsA2A: boolean;
  };
  testDetails?: {
    basicHealthTest: boolean;
    conversationTest: boolean;
    streamingTest: boolean;
    a2aCapable: boolean;
  };
}

interface UseAgentHealthOptions {
  agents: string[];
  checkInterval?: number; // in milliseconds
  enablePeriodicChecking?: boolean;
}

export const useAgentHealth = ({
  agents,
  checkInterval = 30000, // 30 seconds default
  enablePeriodicChecking = true
}: UseAgentHealthOptions) => {
  const [healthStatuses, setHealthStatuses] = useState<Map<string, AgentHealthStatus>>(new Map());
  const [isChecking, setIsChecking] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize health statuses for all agents
  useEffect(() => {
    const initialStatuses = new Map<string, AgentHealthStatus>();
    agents.forEach(agentId => {
      initialStatuses.set(agentId, {
        agentId,
        status: 'unknown',
        lastChecked: null
      });
    });
    setHealthStatuses(initialStatuses);
  }, [agents]);

  // Check health for a single agent
  const checkAgentHealth = useCallback(async (agentId: string): Promise<AgentHealthStatus> => {
    const startTime = Date.now();
    
    try {
      setHealthStatuses(prev => new Map(prev).set(agentId, {
        ...prev.get(agentId)!,
        status: 'checking'
      }));

      const response = await apiService.checkAgentHealth(agentId);
      const responseTime = Date.now() - startTime;
      
      const healthStatus: AgentHealthStatus = {
        agentId,
        status: response.status === 'healthy' ? 'healthy' : 'unhealthy',
        lastChecked: new Date(),
        responseTime,
        error: response.status !== 'healthy' ? response.error : undefined
      };

      setHealthStatuses(prev => new Map(prev).set(agentId, healthStatus));
      return healthStatus;
      
    } catch (error) {
      const responseTime = Date.now() - startTime;
      const healthStatus: AgentHealthStatus = {
        agentId,
        status: 'unhealthy',
        lastChecked: new Date(),
        responseTime,
        error: error instanceof Error ? error.message : 'Health check failed'
      };

      setHealthStatuses(prev => new Map(prev).set(agentId, healthStatus));
      return healthStatus;
    }
  }, []);

  // Check health for all agents
  const checkAllAgents = useCallback(async (): Promise<void> => {
    setIsChecking(true);
    
    try {
      const promises = agents.map(agentId => checkAgentHealth(agentId));
      await Promise.allSettled(promises);
    } finally {
      setIsChecking(false);
    }
  }, [agents, checkAgentHealth]);

  // Manual refresh
  const refreshHealth = useCallback(() => {
    checkAllAgents();
  }, [checkAllAgents]);

  // Get health status for a specific agent
  const getAgentHealth = useCallback((agentId: string): AgentHealthStatus | undefined => {
    return healthStatuses.get(agentId);
  }, [healthStatuses]);

  // Get overall health summary
  const getHealthSummary = useCallback(() => {
    const statuses = Array.from(healthStatuses.values());
    return {
      total: statuses.length,
      healthy: statuses.filter(s => s.status === 'healthy').length,
      unhealthy: statuses.filter(s => s.status === 'unhealthy').length,
      checking: statuses.filter(s => s.status === 'checking').length,
      unknown: statuses.filter(s => s.status === 'unknown').length
    };
  }, [healthStatuses]);

  // Setup periodic health checking
  useEffect(() => {
    if (!enablePeriodicChecking || agents.length === 0) {
      return;
    }

    // Initial check
    checkAllAgents();

    // Setup interval
    intervalRef.current = setInterval(checkAllAgents, checkInterval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enablePeriodicChecking, checkInterval, checkAllAgents, agents.length]);

  return {
    healthStatuses: Array.from(healthStatuses.values()),
    isChecking,
    checkAgentHealth,
    checkAllAgents,
    refreshHealth,
    getAgentHealth,
    getHealthSummary
  };
}; 